import { useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

/**
 * Generic admin CRUD list + form.
 *
 * Props:
 * - title: section heading
 * - endpoint: e.g. "courses" (resolves to /admin/courses)
 * - fields: [{ name, label, type, options?, placeholder?, full?, required? }]
 * - testIdPrefix: e.g. "course"
 * - renderRow(item): JSX for the row main content
 */
export default function CrudSection({
  title,
  endpoint,
  fields,
  testIdPrefix,
  renderRow,
  defaultValues = {},
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultValues);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/${endpoint}`);
      setItems(data);
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint]);

  function startCreate() {
    setEditing("new");
    setForm({ ...defaultValues });
    setErr("");
  }

  function startEdit(item) {
    setEditing(item.id);
    const f = { ...defaultValues };
    fields.forEach((fl) => {
      f[fl.name] = item[fl.name] ?? defaultValues[fl.name] ?? "";
    });
    setForm(f);
    setErr("");
  }

  function cancel() {
    setEditing(null);
    setForm({ ...defaultValues });
    setErr("");
  }

  async function save() {
    setBusy(true);
    setErr("");
    try {
      const payload = { ...form };
      // coerce numeric/boolean; empty string -> null (so Optional[int] works)
      fields.forEach((f) => {
        if (f.type === "number") payload[f.name] = Number(payload[f.name] || 0);
        if (f.type === "checkbox") payload[f.name] = Boolean(payload[f.name]);
      });
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      // Auto-coerce known integer fields
      if (payload.grade != null && payload.grade !== "") {
        const g = Number(payload.grade);
        payload.grade = Number.isFinite(g) ? g : null;
      }
      if (editing === "new") {
        await api.post(`/admin/${endpoint}`, payload);
      } else {
        await api.patch(`/admin/${endpoint}/${editing}`, payload);
      }
      cancel();
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id) {
    if (!window.confirm("Delete this item? This action cannot be undone.")) return;
    try {
      await api.delete(`/admin/${endpoint}/${id}`);
      load();
    } catch (e) {
      alert(formatApiError(e));
    }
  }

  return (
    <section data-testid={`${testIdPrefix}-section`}>
      <header className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-black tracking-tight text-[#1F2547]">
            {title}
          </h1>
          <p className="text-sm text-[#5B6079] font-medium mt-1">
            {items.length} item{items.length === 1 ? "" : "s"}
          </p>
        </div>
        <button
          data-testid={`${testIdPrefix}-add-btn`}
          onClick={startCreate}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#1F2547] text-white font-bold rounded-full brutal-shadow-sm btn-press"
        >
          <Plus className="w-4 h-4" /> Add new
        </button>
      </header>

      {editing !== null && (
        <div
          data-testid={`${testIdPrefix}-form`}
          className="mb-6 rounded-2xl bg-white brutal-shadow p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display font-bold text-xl text-[#1F2547]">
              {editing === "new" ? "Create" : "Edit"}
            </h2>
            <button
              onClick={cancel}
              className="text-[#5B6079] hover:text-[#1F2547]"
              aria-label="Cancel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f.name} className={f.full ? "md:col-span-2" : ""}>
                <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                  {f.label}
                </label>
                {f.type === "textarea" ? (
                  <textarea
                    data-testid={`${testIdPrefix}-field-${f.name}`}
                    value={form[f.name] || ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
                    placeholder={f.placeholder}
                  />
                ) : f.type === "select" ? (
                  <select
                    data-testid={`${testIdPrefix}-field-${f.name}`}
                    value={form[f.name] || ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium bg-white"
                  >
                    {f.options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : f.type === "checkbox" ? (
                  <label className="inline-flex items-center gap-2 mt-2">
                    <input
                      data-testid={`${testIdPrefix}-field-${f.name}`}
                      type="checkbox"
                      checked={!!form[f.name]}
                      onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })}
                      className="w-4 h-4 accent-[#9F87D8]"
                    />
                    <span className="text-sm text-[#1F2547] font-medium">
                      {f.placeholder || "Enabled"}
                    </span>
                  </label>
                ) : (
                  <input
                    data-testid={`${testIdPrefix}-field-${f.name}`}
                    type={f.type || "text"}
                    value={form[f.name] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
                    placeholder={f.placeholder}
                  />
                )}
              </div>
            ))}
          </div>

          {err && (
            <div
              data-testid={`${testIdPrefix}-form-error`}
              className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 font-medium"
            >
              {err}
            </div>
          )}

          <div className="mt-5 flex gap-3">
            <button
              data-testid={`${testIdPrefix}-save-btn`}
              onClick={save}
              disabled={busy}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#9F87D8] text-white font-bold rounded-full brutal-shadow-sm btn-press disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Save
            </button>
            <button
              onClick={cancel}
              className="px-5 py-2.5 bg-[#EFE9F7] text-[#1F2547] font-bold rounded-full btn-press"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-[#5B6079] font-medium">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-[#5B6079] font-medium bg-white rounded-2xl p-6 brutal-shadow-sm">
          No items yet. Click <b>Add new</b> to create one.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div
              key={item.id}
              data-testid={`${testIdPrefix}-row-${item.id}`}
              className="bg-white rounded-2xl brutal-shadow-sm p-5 flex items-center gap-4"
            >
              <div className="flex-1 min-w-0">{renderRow(item)}</div>
              <div className="flex items-center gap-2">
                <button
                  data-testid={`${testIdPrefix}-edit-${item.id}`}
                  onClick={() => startEdit(item)}
                  className="w-9 h-9 rounded-full bg-[#EFE9F7] text-[#1F2547] flex items-center justify-center hover:bg-[#9F87D8] hover:text-white transition-colors"
                  aria-label="Edit"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  data-testid={`${testIdPrefix}-delete-${item.id}`}
                  onClick={() => remove(item.id)}
                  className="w-9 h-9 rounded-full bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-600 hover:text-white transition-colors"
                  aria-label="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
